/* eslint-disable @next/next/no-img-element */
import { unit, C, Canvas, Abs, Card, Avatar, BLogo, Icon, Arrows, Sparkle } from './kit';

// Vector rebuild of /img/redesign/perform-visual-v3.png (1496×1051 canvas).
// Each tilted card is laid out un-rotated (coordinates measured on a
// de-rotated copy of the PNG) and then rotated around its own centre.
const W = 1496;
const H = 1051;
const u = unit(W);

const SLATE = '#4B5279';
const PEACH = '#F7A585';
const GREEN = '#3DAE48';

// Single-line text whose cap-height centre sits at `y`.
const T = ({ x, y, s, w = 500, c = C.ink, style, children }) => (
  <Abs u={u} x={x} y={y} style={{
    transform: 'translateY(-50%)', fontSize: u(s), fontWeight: w, color: c, lineHeight: 1, ...style,
  }}>
    {children}
  </Abs>
);

const Box = ({ x, y, w, h, r = 12, bg = '#FCFCFC', border = '#ECECF0', style, children }) => (
  <Abs u={u} x={x} y={y} w={w} h={h} style={{
    background: bg, borderRadius: u(r), border: `${u(1.5)} solid ${border}`, boxSizing: 'border-box', ...style,
  }}>
    {children}
  </Abs>
);

const Svg = ({ size, children, style, viewBox = '0 0 24 24' }) => (
  <svg viewBox={viewBox} style={{ width: u(size), height: u(size), display: 'block', ...style }} aria-hidden="true">
    {children}
  </svg>
);

const StarShape = ({ id, fill = 1 }) => (
  <>
    {fill < 1 && (
      <defs>
        <linearGradient id={id}>
          <stop offset={fill} stopColor="#F9B92C" />
          <stop offset={fill} stopColor="#ECE9EF" />
        </linearGradient>
      </defs>
    )}
    <path
      d="M12 2.6l2.85 5.9 6.45.85-4.7 4.5 1.17 6.42L12 17.2l-5.77 3.07 1.17-6.42-4.7-4.5 6.45-.85z"
      fill={fill < 1 ? `url(#${id})` : '#F9B92C'}
      stroke={fill < 1 ? `url(#${id})` : '#F9B92C'}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </>
);

const Face = ({ x, y, size = 34, from, to, mood, ink }) => (
  <Abs u={u} x={x - size / 2} y={y - size / 2} w={size} h={size}>
    <Svg size={size} viewBox="0 0 32 32">
      <defs>
        <radialGradient id={`face-${x}`} cx="38%" cy="32%" r="75%">
          <stop offset="0" stopColor={from} />
          <stop offset="1" stopColor={to} />
        </radialGradient>
      </defs>
      <circle cx="16" cy="16" r="15.5" fill={`url(#face-${x})`} />
      <ellipse cx="11.4" cy="13.2" rx="1.5" ry="1.9" fill={ink} />
      <ellipse cx="20.6" cy="13.2" rx="1.5" ry="1.9" fill={ink} />
      {mood === 'sad' && <path d="M10.6 22.4c1.5-2.4 3.3-3.4 5.4-3.4s3.9 1 5.4 3.4" fill="none" stroke={ink} strokeWidth="1.7" strokeLinecap="round" />}
      {mood === 'meh' && <path d="M11 20.3h10" fill="none" stroke={ink} strokeWidth="1.7" strokeLinecap="round" />}
      {mood === 'happy' && <path d="M10 18.6c1.5 3 3.5 4.3 6 4.3s4.5-1.3 6-4.3" fill="none" stroke={ink} strokeWidth="1.9" strokeLinecap="round" />}
    </Svg>
  </Abs>
);

const Check = ({ x, y }) => (
  <Abs u={u} x={x - 9} y={y - 9} w={18} h={18} style={{
    borderRadius: '50%', background: '#CDE9B9', color: '#3E9A45',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <Icon u={u} name="check" size={11} stroke={3.4} />
  </Abs>
);

const SUMMARY = [
  { y: 580, title: 'Goal Achievement', sub: 'Exceeds expectations', pct: 92, fill: 222, icon: 'stack' },
  { y: 655, title: 'Core Competencies', sub: 'Great strengths', pct: 88, fill: 205, icon: 'heart' },
  { y: 728, title: 'Growth & Development', sub: 'Keep growing', pct: 70, fill: 163, icon: 'trend' },
];

const SummaryIcon = ({ name }) => {
  if (name === 'heart') {
    return (
      <Svg size={26}>
        <path d="M12 20.6S3 15.2 3 9.2A4.7 4.7 0 0 1 12 7.1a4.7 4.7 0 0 1 9 2.1c0 6-9 11.4-9 11.4z" fill="#F2511F" />
      </Svg>
    );
  }
  if (name === 'trend') {
    return (
      <Svg size={26}>
        <path d="M3 17.5l6-6 4 4 7.5-7.5M15 7.5h5.8v5.8" fill="none" stroke="#F2511F" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      </Svg>
    );
  }
  return (
    <Svg size={26}>
      <path d="M4 15.5c0 2.2 3.6 3.8 8 3.8s8-1.6 8-3.8v-2.8H4z" fill="#F2511F" />
      <ellipse cx="12" cy="12.7" rx="8" ry="3.2" fill="#F7835A" />
      <path d="M6 10.8c0 1.8 2.7 3 6 3s6-1.2 6-3V8.4H6z" fill="#F2511F" />
      <ellipse cx="12" cy="8.4" rx="6" ry="2.6" fill="#FA9670" />
      <path d="M9 5.6c1-1.5 5-1.5 6 0" fill="none" stroke="#F2511F" strokeWidth="1.6" strokeLinecap="round" />
    </Svg>
  );
};

const FEEDBACK = [
  { label: 'Manager', value: '4.6', color: GREEN },
  { label: 'Peers', value: '4.3', color: '#A8D08D' },
  { label: 'Direct Reports', value: '4.4', color: '#F9C440' },
  { label: 'Self', value: '4.6', color: '#F25A2A' },
];

// Donut segments, clockwise from 12 o'clock, in degrees.
const DONUT = [
  { from: 0, to: 90, color: '#45B649' },
  { from: 90, to: 127, color: '#A9D18F' },
  { from: 127, to: 180, color: '#45B649' },
  { from: 180, to: 360, color: '#A9D18F' },
];

const arc = (cx, cy, r, a0, a1) => {
  const p = (a) => {
    const rad = ((a - 90) * Math.PI) / 180;
    return `${(cx + r * Math.cos(rad)).toFixed(2)} ${(cy + r * Math.sin(rad)).toFixed(2)}`;
  };
  return `M${p(a0)} A${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${p(a1)}`;
};

export default function PerformVisual({ className }) {
  return (
    <Canvas W={W} H={H} className={className} label="Performance review dashboard with an AI Coach tip, review summary, 360° feedback ratings, a weekly check-in and AI-powered review insights">
      <Sparkle u={u} x={160} y={103} size={24} color={PEACH} />
      <Sparkle u={u} x={105} y={168} size={21} color={PEACH} />
      <Sparkle u={u} x={1462} y={422} size={24} color={PEACH} />

      {/* Main review dashboard */}
      <Card u={u} x={190} y={45} w={860} h={842} r={26} bg="#FDFDFD" style={{ transform: 'rotate(1.7deg)' }}>
        {/* Sidebar */}
        <Abs u={u} x={7} y={10} w={93} h={626} style={{ background: '#1A1332', borderRadius: u(20) }}>
          <Abs u={u} x={17} y={18}><BLogo u={u} size={58} /></Abs>
          {[['home', 125], ['chat', 183], ['hash', 246], ['users', 309], ['chart', 371], ['gear', 433]].map(([name, cy]) => (
            <Abs key={name} u={u} x={11} y={cy - 25} w={70} h={50} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: u(11),
              background: name === 'hash' ? '#6F48FB' : 'transparent', color: name === 'home' || name === 'hash' ? '#fff' : '#DCDAE6',
            }}>
              {name === 'chat' ? (
                <Svg size={32}>
                  <path d="M5 3.5h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-8.2L6 21.3v-3.8H5a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2z" fill="#DCDAE6" />
                  <path d="M8.5 10.5h7" stroke="#1A1332" strokeWidth="2.2" strokeLinecap="round" />
                </Svg>
              ) : (
                <Icon u={u} name={name} size={name === 'hash' ? 34 : 32} stroke={name === 'hash' ? 2.8 : 2.4} />
              )}
            </Abs>
          ))}
          <Abs u={u} x={15} y={534}><Avatar u={u} name="me" size={62} ring={2.5} dot="#1A1332" /></Abs>
        </Abs>

        {/* Header + tabs */}
        <T x={152} y={58} s={28} w={700} style={{ letterSpacing: '-0.005em' }}>Performance Review</T>
        <T x={170} y={112} s={16.5} w={600} c="#FE5B25">Review</T>
        <T x={278} y={112} s={16.5} c={SLATE}>360° Feedback</T>
        <T x={440} y={113} s={16.5} c={SLATE}>History</T>
        <Abs u={u} x={151} y={135} w={86} h={3} style={{ background: '#FE5B25', borderRadius: u(2) }} />
        <Abs u={u} x={151} y={141} w={664} h={1.5} style={{ background: '#F1F1F4' }} />
        <Abs u={u} x={686} y={38} w={124} h={39} style={{
          background: '#FDEFEC', borderRadius: u(20), display: 'flex', alignItems: 'center',
          gap: u(7), paddingLeft: u(16), boxSizing: 'border-box', fontSize: u(14.5), fontWeight: 700,
        }}>
          <Svg size={19}>
            <path d="M12 1.5c.7 5.6 4.9 9.8 10.5 10.5-5.6.7-9.8 4.9-10.5 10.5C11.3 16.9 7.1 12.7 1.5 12 7.1 11.3 11.3 7.1 12 1.5z" fill="#5B3DF5" />
          </Svg>
          AI Coach
        </Abs>

        {/* Completion banner */}
        <Box x={152} y={167} w={660} h={83} bg="#F7F9F6" border="#E9EDE7">
          <Abs u={u} x={20} y={19} w={40} h={40} style={{
            borderRadius: '50%', background: GREEN, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon u={u} name="check" size={22} stroke={3} />
          </Abs>
          <T x={81} y={29} s={18.8} w={700}>60/60 performance reviews completed</T>
          <T x={81} y={54} s={15} c="#3D4166">Great work! Your team has completed all reviews on time.</T>
          <img src="/img/redesign/visual-parts/perform-confetti.webp" alt="" draggable="false" style={{
            position: 'absolute', left: u(531), top: u(13), width: u(112), height: u(67),
          }} />
        </Box>

        {/* Employee */}
        <Abs u={u} x={157} y={272}><Avatar u={u} name="john" size={84} /></Abs>
        <T x={262} y={301} s={29.4} w={700} style={{ letterSpacing: '-0.005em' }}>John Smith</T>
        <T x={263} y={335} s={19} c={SLATE}>Account Executive</T>
        <Abs u={u} x={695} y={297} w={119} h={38} style={{
          background: '#E8F4E4', borderRadius: u(19), display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: `0 ${u(15)} 0 ${u(16)}`, boxSizing: 'border-box',
          fontSize: u(16), fontWeight: 600, color: '#2F9A37',
        }}>
          On Track
          <span style={{ width: u(12), height: u(12), borderRadius: '50%', background: GREEN }} />
        </Abs>

        {/* Overall rating + AI Coach tip */}
        <Box x={154} y={373} w={662} h={160} r={14}>
          <T x={30} y={35} s={18.6} w={700}>Overall Rating</T>
          <T x={31} y={83} s={39} w={700} style={{ letterSpacing: '-0.01em' }}>4.6</T>
          <Abs u={u} x={104} y={76} style={{ display: 'flex', gap: u(2.5) }}>
            {[1, 1, 1, 1, 0.55].map((f, i) => (
              <Svg key={i} size={23}><StarShape id="perform-half-star" fill={f} /></Svg>
            ))}
          </Abs>
          <T x={31} y={123} s={17.4}>Great Performance!</T>

          <Abs u={u} x={283} y={17} w={355} h={122} style={{ background: '#F0EEFD', borderRadius: u(10) }}>
            <img src="/img/redesign/visual-parts/perform-coach.webp" alt="" draggable="false" style={{
              position: 'absolute', left: u(7), top: u(13), width: u(88), height: u(92),
            }} />
            <Abs u={u} x={103} y={0} w={252} h={122} style={{ background: '#D3CCFC', borderRadius: u(10) }}>
              <Abs u={u} x={-8} y={97} w={0} h={0} style={{
                borderTop: `${u(7)} solid transparent`, borderBottom: `${u(7)} solid transparent`,
                borderRight: `${u(9)} solid #D3CCFC`,
              }} />
              <T x={18} y={27} s={15.5} w={700}>AI Coach</T>
              <Abs u={u} x={18} y={44} style={{ fontSize: u(14.5), lineHeight: u(23.2), color: '#2A2A40' }}>
                John is making strong progress.<br />Consider recognizing his<br />client impact.
              </Abs>
            </Abs>
          </Abs>
        </Box>

        {/* Review summary */}
        <Box x={154} y={538} w={662} h={298} r={14}>
          <T x={30} y={23} s={18} w={700}>Review Summary</T>
        </Box>
        {SUMMARY.map((row) => (
          <Box key={row.title} x={178} y={row.y} w={617} h={70} r={10} bg="#FDFCFD" border="#EEEDF1">
            <Abs u={u} x={15} y={13} w={44} h={44} style={{
              borderRadius: '50%', background: '#FDE4DA', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SummaryIcon name={row.icon} />
            </Abs>
            <T x={80} y={26} s={16} w={700}>{row.title}</T>
            <T x={80} y={50} s={14.5} c={SLATE}>{row.sub}</T>
            <Abs u={u} x={305} y={31.5} w={234} h={9} style={{ background: '#EFEEF2', borderRadius: u(4.5) }}>
              <Abs u={u} x={0} y={0} w={row.fill} h={9} style={{ background: 'linear-gradient(180deg, #4CBC4F, #3AA841)', borderRadius: u(4) }} />
            </Abs>
            <T x={561} y={36} s={16} w={700}>{row.pct}%</T>
          </Box>
        ))}
      </Card>

      {/* 360° Feedback */}
      <Card u={u} x={1079} y={169} w={364} h={277} r={22} style={{ transform: 'rotate(2.8deg)' }}>
        <T x={27} y={43} s={23} w={700} style={{ letterSpacing: '-0.005em' }}>360° Feedback</T>
        <Abs u={u} x={23.5} y={90.5} w={145} h={145}>
          <svg viewBox="0 0 145 145" style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden="true">
            {DONUT.map((s) => (
              <path key={s.from} d={arc(72.5, 72.5, 61.5, s.from, s.to)} fill="none" stroke={s.color} strokeWidth="22" />
            ))}
          </svg>
        </Abs>
        <T x={46} y={151} s={40} w={700} style={{ width: u(100), textAlign: 'center', letterSpacing: '-0.01em' }}>4.5</T>
        <T x={46} y={183} s={14} w={700} style={{ width: u(100), textAlign: 'center' }}>Avg. Rating</T>
        {FEEDBACK.map((f, i) => {
          const y = 109 + i * 37;
          return (
            <div key={f.label}>
              <Abs u={u} x={192} y={y - 6.5} w={13} h={13} style={{ borderRadius: '50%', background: f.color }} />
              <T x={214} y={y} s={14.6}>{f.label}</T>
              <T x={297} y={y + 1} s={16} c={SLATE} style={{ width: u(40), textAlign: 'right' }}>{f.value}</T>
            </div>
          );
        })}
      </Card>

      {/* Weekly check-in */}
      <Card u={u} x={16} y={666} w={309} h={324} r={20} style={{ transform: 'rotate(-2.6deg)' }}>
        <T x={28} y={39} s={18.8} w={700}>Weekly Check-in</T>
        <Sparkle u={u} x={273} y={37} size={19} color={PEACH} />
        <Abs u={u} x={27} y={70} style={{ fontSize: u(15), lineHeight: u(21.5), color: '#22243A' }}>
          How are you feeling about<br />your progress this week?
        </Abs>
        <Face x={45} y={153} from="#FDC2AC" to="#F28A6C" mood="sad" ink="#8A3520" />
        <Face x={98} y={153} from="#FDC6B0" to="#F08E70" mood="sad" ink="#8A3520" />
        <Face x={151} y={153} from="#FFE48E" to="#F5BC45" mood="meh" ink="#6E4E12" />
        <Abs u={u} x={181} y={127} w={53} h={51} style={{
          border: `${u(1.5)} solid #74C870`, borderRadius: u(6), boxSizing: 'border-box',
        }} />
        <Face x={207.5} y={153} from="#6FD458" to="#1F9A35" mood="happy" ink="#103F16" />
        <Face x={263.5} y={153} from="#BDEE94" to="#72C94E" mood="happy" ink="#1A4A18" />
        <Abs u={u} x={22} y={197} w={276} h={49} style={{
          background: '#F6F3F4', borderRadius: u(6), border: `${u(1)} solid #EEEAEC`, boxSizing: 'border-box',
        }}>
          <T x={13} y={24} s={12.5} c="#6A6F90">Add a note (optional)...</T>
        </Abs>
        <Abs u={u} x={22} y={263} w={137} h={36} style={{
          background: 'linear-gradient(180deg, #FF6B3A, #F4511E)', borderRadius: u(6), color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: u(14), fontWeight: 700,
          boxShadow: `0 ${u(3)} ${u(8)} rgba(243, 90, 43, 0.25)`,
        }}>
          Submit Check-in
        </Abs>
      </Card>

      {/* AI-powered review */}
      <Card u={u} x={876} y={773} w={530} h={229} r={22} bg="#FDF0EE" style={{ transform: 'rotate(3.2deg)', border: `${u(1.5)} solid #F7E3DE` }}>
        <T x={35} y={40} s={20} w={700}>AI-Powered Review</T>
        <Sparkle u={u} x={227} y={38} size={19} color={PEACH} />
        {['AI Coach suggestions', 'Recognition based on impact', 'Bias-free, consistent scoring', 'Actionable growth insights'].map((t, i) => {
          const y = 82 + i * 37;
          return (
            <div key={t}>
              <Check x={43} y={y} />
              <T x={64} y={y} s={16} c="#1E2240">{t}</T>
            </div>
          );
        })}
        <img src="/img/redesign/visual-parts/perform-ball.webp" alt="" draggable="false" style={{
          position: 'absolute', left: u(290), top: u(39), width: u(118), height: u(132),
        }} />
        <img src="/img/redesign/mascots/clappy.webp" alt="" draggable="false" style={{
          position: 'absolute', left: u(399), top: u(29), width: u(124), height: u(124),
        }} />
      </Card>
      {/* Connector arrows sit on top: the bottom one points into the AI card. */}
      <Arrows W={W} H={H} arrows={[
        { d: 'M147 398 C95 392 70 345 76 310 C80 282 100 262 132 257', color: PEACH, head: [135, 257, -10] },
        { d: 'M1455 247 C1470 236 1490 214 1484 180', color: PEACH, head: [1482, 173, -102] },
        { d: 'M1393 680 C1405 712 1402 752 1367 786', color: PEACH, head: [1361, 792, 135] },
      ]} />
    </Canvas>
  );
}
